import { Component, OnInit, ViewChild, AfterViewInit } from '@angular/core';
import { BroadcastService, MsalService } from '@azure/msal-angular';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

const SEARCH_QUERY_DEBOUNCE_TIMEOUT = 500;
import { LoginAwareComponent } from '../../../../login-aware-component';
import { DiagnosticsServiceFactory } from '../../../../services/diagnostics-service-factory';
import { QnAService } from '../../../../services/qna-service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { SP_SCOPES, GRAPH_SCOPES } from '../../../../msal.constants';
import { SupportRequestModalComponent } from '../../modals/support-request/support-request.modal.component';
import { TeamsTrainingVideosModalComponent } from '../../modals/teams-training-videos/teams-training-videos.modal.component';
import { HomePageInstructionsModalComponent } from '../../modals/homepage-instructions/homepage-instructions.modal.component';
import { BackBeaconService } from '../../../shared/services/back-beacon.service';
import { GraphClientService } from '../../../../services/graph-client-service';

declare var widget;

/**
 *
 * */
@Component({
  selector: 'campfire-faq-panel',
  templateUrl: './faq-panel.component.html',
  styleUrls: ['./faq-panel.component.scss']
})
export class FaqPanelComponent extends LoginAwareComponent implements OnInit, AfterViewInit  {
  public originalRefinerItems: any[] = [];
  public refinerItems: any[];
  public refinerOptions: any;

  public refinerValue: string;
  public queryText: string;

  private searchTerms: Subject<string>;

  private searchResults: any[] = [];

  private department: string = null;

  private employeeType: string = null;

  public showJobPostings: boolean = false;

  constructor(diagnosticsFactory: DiagnosticsServiceFactory,
    broadcastService: BroadcastService,
    authService: MsalService,
    private graphClient: GraphClientService,
    private _service: QnAService,
    private _modalService: NgbModal,
    private beaconService: BackBeaconService) {
    super(diagnosticsFactory.create('FaqPanelComponent'), broadcastService, authService)
    this.refinerOptions = {
      idField: 'value',
    };

    this.refinerItems = [];
    this.queryText = null;
    this.refinerValue = null;

    // Setting debouncer for taking text from search textbox as the user types it in...
    this.searchTerms = new Subject<string>();
    this.searchTerms.pipe(
      debounceTime(SEARCH_QUERY_DEBOUNCE_TIMEOUT)).subscribe((text) => {
        this.queryText = text;
        this.refinerValue = null;
        this._runQuery(this.queryText, this.refinerValue);
      });
  }
    ngAfterViewInit(): void {
      widget({
        "company_code": "Navigatr",
        "bg_color_widget": "#ffffff",
        "bg_color_headers": "#969696",
        "bg_color_links": "#55dd88",
        "txt_color_headers": "#292929",
        "txt_color_job": "#3d3d3d",
        "bg_color_even_row": "#e0e0e0",
        "bg_color_odd_row": "#f7f7f7",
        "auto_width": "auto",
        "auto_height": "auto",
        "number": "on",
        "job_title": "true",
        "location": "true",
        "dateFormat": "us",
        "departments_field_id": "5b6089b6b60a5e5791ab6b23",
        "jobAdType": "INTERNAL_OR_PUBLIC",
        "auth": "3aabdc10da814d23aaadda8936be207e",
        "trid": "",
        "api_url": "https://www.smartrecruiters.com",
        "custom_css_url": "https://static.smartrecruiters.com/job-widget/1.6.1/css/smart_widget.css"
      });

    }

  ngOnInit() {
    super.ngOnInit();
    this._runQuery();

    if (this.loggedIn) {
      this.fetchDepartment();
    }
  }

  formatDetailedAnswer(answer): string {
    return answer
      .replace(/http\/\//g, 'http://')
      .replace(/https\/\//g, 'https://');
  }

  onRefinerSelected(refiner) {
    const value = refiner.value === this.refinerValue ? null : refiner.value;

    this._runQuery(this.queryText, value).then(() => {
      this.refinerValue = value;
    });
  }

  onLogin(): void {
    this.authService.loginPopup();
  }

  onSearchTextChange(event) {
    this.refinerValue = null;
    const text = event.target.value;
    this.searchTerms.next(text);
  }

  onSupportRequestClicked(): void {
    this._modalService.open(SupportRequestModalComponent, { size: 'lg', backdropClass: 'white-backdrop' });
  }

  onTeamsTrainingVideosSelected(): void {
    this._modalService.open(TeamsTrainingVideosModalComponent, { size: 'lg', backdropClass: 'white-backdrop' });
  }

  onCampfireHomepageSelected(): void {
    this._modalService.open(HomePageInstructionsModalComponent, { size: 'lg', backdropClass: 'white-backdrop' });
  }

  get isKtAgent(): boolean {
    if (!this.loggedIn || !this.userInfo || !this.userInfo.displayableId) {
      return false;
    }

    return this.userInfo.displayableId.indexOf('@kensingtontours.') !== -1;
  }

  get isStaff(): boolean {
    return this.employeeType === 'staff';
  }

  get canViewStaffLinks(): boolean {
    if (!this.loggedIn || !this.userInfo || !this.userInfo.displayableId) {
      return false;
    }

    if (!this.isStaff) {
      return false;
    }

    let validEmailSuffix =
      this.userInfo.displayableId.indexOf('@traveledge.') !== -1 ||
      this.userInfo.displayableId.indexOf('@trip-arc.') !== -1;

    return validEmailSuffix;
  }

  get firstName(): string | null {
    if (!this.loggedIn || !this.userInfo || !this.userInfo.name) {
      return null;
    }

    return this.userInfo.name.split(' ')[0];
  }

  get showResults(): boolean {
    return !!(this.queryText || this.refinerValue);
  }

  protected onLoginSuccess(token: any) {
    super.onLoginSuccess(token);
    this._runQuery();
    this.fetchDepartment();

    if (this.userInfo) {

    }
  }

  private _runQuery(query: string = null, refiner: string = null) {
    if (!this.loggedIn) {
      return;
    }

    return this.authorize(SP_SCOPES).then(token => {
      return this._service.runQuery(this.constructor.name, query, refiner, token).toPromise()
        .then(result => {
          if (!this.originalRefinerItems.length) {
            // Only save refiners once, because we don't want to filter them away as we are
            // changing query, filtering, etc.
            this.refinerItems = result.searchRefiners;

            for (let item of result.searchRefiners) {
              this.originalRefinerItems.push({ name: item.name, value: item.value });
            }
          } else if (!query) {
            // If we do not have a query, then we always want to show the full count of refiners.
            for (let refiner of this.originalRefinerItems) {
              const foundRefiner = this.refinerItems.find(r => r.value === refiner.value);
              foundRefiner.name = refiner.name;
            }
          } else {
            // We have a query, so lets go ahead and update the refiners with what comes back from SharePoint.
            for (let existingRefiner of this.refinerItems) {
              const foundRefiner = result.searchRefiners.find(r => r.value === existingRefiner.value);

              if (foundRefiner) {
                existingRefiner.name = foundRefiner.name;
              } else {
                existingRefiner.name = `${existingRefiner.value} (0)`;
              }
            }
          }

          this.searchResults = result.searchResults;
        })
        .catch(error => {
          this.diagnostics.logError(`Error loading data. Details: ${JSON.stringify(error)}`);
        });
    });
  }

  private fetchDepartment(): void {
    this.authorize(GRAPH_SCOPES).then(token => {
      this.graphClient.getData('GetDepartment', token).subscribe(data => {
        this.department = data['department'];
        this.employeeType = data['extension_9f6a03dbdfad4df186b50b40731e3684_employeeType'];
      });

      this.graphClient.getData('GetCompanyUsers', token).subscribe((data: any) => {
        let ids = (<any[]>data.value).map(v => v.id) as string[];

        this.graphClient.postData('GetPresencesByIds', { ids: ids }, token).subscribe((resp: any) => {
          (<any[]>resp.value).forEach (v => {
            let user = (<any[]>data.value).find(t => t.id === v.id);
            user.activity = v.activity;
            user.availability = v.availability;
          });


          console.log(data);
        });
      });
    });
  }

  onJobPostingsClicked(): void {
    this.showJobPostings = !this.showJobPostings;
  }
}
